import {
  IBrand,
  IItem,
  ISupplier,
  ISupplierAlias,
  Item,
  IUnit,
  IItemSupplierVariant,
  IStocktake,
} from '$models'
import {
  brandService,
  itemService,
  itemSupplierVariantService,
  stocktakeService,
  supplierService,
  unitService,
} from '$services'
import { enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { StateCreator } from 'zustand'
import { arrayToDictionary } from './invoices/helper'
import { SharedSlice } from 'src/components/slice'
import { updateOrAddItemSupplierVariantWithinItemStore } from './helper'

interface StockSharedState {
  suppliers: Record<string, ISupplier>
  items: Record<string, IItem>
  brands: Record<string, IBrand>
  units: Record<string, IUnit>
  activeSupplierAliases: ISupplierAlias[]
  isLoadingActiveSupplierAliases: boolean
}

export interface StockSharedSlice extends StockSharedState {
  /**
   * Get all suppliers
   * @param forceDatabaseFetch - Force a database fetch even if suppliers are cached
   * @param includeDeleted - Whether to include deleted suppliers
   */
  getSuppliers: (
    forceDatabaseFetch?: boolean,
    includeDeleted?: boolean
  ) => Promise<Record<string, ISupplier> | null>

  /**
   * Get all items
   */
  getItems: () => Promise<Record<string, IItem> | null>

  /**
   * Get all items
   */
  updateItemState: (updatedItem: IItem) => Promise<IItem | null>

  /**
   * Get all brands
   */
  getBrands: () => Promise<Record<string, IBrand> | null>

  /**
   * Get all units
   * @param includeDeleted - Whether or not to include deleted units
   */
  getStockUnits: (
    includeDeleted?: boolean
  ) => Promise<Record<string, IUnit> | null>

  /**
   * Creates a new stock unit in Loaded
   * @param stockUnit the stock unit to be created
   * @returns the newly created stock unit
   */
  createStockUnit: (stockUnit: IUnit) => Promise<IUnit | null>

  /**
   * Updates an item supplier variant
   * @param updatedItemSupplierVariant the updated item supplier variant
   * @returns the updated item supplier variant
   */
  updateItemSupplierVariant: (
    updatedItemSupplierVariant: IItemSupplierVariant
  ) => Promise<IItemSupplierVariant | null>

  /**
   * @param item - The item to update.
   * @returns A promise that resolves to the updated item.
   */
  updateItem: (item: IItem) => Promise<IItem | null>

  /**
   * Updates the stock item, brand, and/or unit of an item supplier variant
   * @param itemSupplierVariantId that will be updated
   * @param stockItemId the stockItemId to update
   * @param brandId the brandId to update
   * @param unitId the unitId to update
   * @returns the updated item spplier variant
   */
  patchItemSupplierVariant: (
    itemSupplierVariantId: string,
    stockItemId?: string,
    brandId?: string,
    unitId?: string
  ) => Promise<IItemSupplierVariant | null>

  /**
   * Creates a new stocktake
   * @param item the stocktake to be created
   * @returns the newly created stocktake
   */
  createStocktake: (stocktake: IStocktake) => Promise<IStocktake | null>

  /**
   * Creates a new item in Loaded
   * @param item the item to be created
   * @returns the newly created item
   */
  createItem: (item: IItem) => Promise<IItem | null>

  /**
   * Creates a new brand in Loaded
   * @param brand the brand to be created
   * @returns the newly created brand
   */
  createBrand: (brand: IBrand) => Promise<IBrand | null>

  /**
   * Creates a new supplier
   * @param newSupplier the new supplier to be created
   * @returns the created supplier
   */
  createSupplier: (supplier: ISupplier) => Promise<ISupplier | null>

  /**
   * Updates a supplier
   * @param supplier the supplier to be updated
   * @returns the updated supplier
   */
  updateSupplier: (supplier: ISupplier) => Promise<ISupplier | null>

  /**
   * Soft deletes a supplier
   * @param supplier the supplier to delete
   * @returns the updated supplier
   */
  deleteSupplier: (supplier: ISupplier) => Promise<ISupplier | null>

  /**
   * Fetches aliases for the given supplier into the active-dialog slot
   */
  loadActiveSupplierAliases: (supplierId: string) => Promise<void>

  /**
   * Deletes a single supplier alias
   * @returns true on success, false on failure
   */
  deleteSupplierAlias: (supplierId: string, aliasId: string) => Promise<boolean>

  /**
   * Restores a soft-deleted supplier
   * @param supplier the supplier to restore
   * @returns the updated supplier
   */
  restoreSupplier: (supplier: ISupplier) => Promise<ISupplier | null>

  /**
   * Merges two suppliers - consolidates source supplier into destination supplier
   * @param sourceId the ID of the source supplier to merge from
   * @param destId the ID of the destination supplier to merge into
   * @returns object containing both updated suppliers
   */
  mergeSuppliers: (
    sourceId: string,
    destId: string
  ) => Promise<{ source: ISupplier; destination: ISupplier } | null>

  /**
   * Retrieves an item supplier variant via its code, item ID, and supplier ID
   * @param code the code of the variant
   * @param itemId the item ID the variant belongs to
   * @param supplierId the supplier ID the variant comes under
   * @returns the related item supplier variant
   */
  getItemSupplierVariant: (
    code: string,
    itemId: string,
    supplierId: string
  ) => Promise<IItemSupplierVariant | null>

  /**
   * Removes (hard deletes) an item supplier variant
   * @param itemSupplierVariantId The Item Supplier Variant ID
   * @param itemId The ID of the item the variant belongs to
   * @returns void
   */
  removeItemSupplierVariant: (
    itemSupplierVariantId: string,
    itemId: string
  ) => Promise<boolean>

  /**
   * Reset the Shared Stock Slice to its inital State
   */
  resetSharedStockSlice: () => void
}

const initialState: StockSharedState = {
  suppliers: {},
  items: {},
  brands: {},
  units: {},
  activeSupplierAliases: [],
  isLoadingActiveSupplierAliases: false,
}

export const createSharedStockSlice: StateCreator<
  StockSharedSlice & SharedSlice,
  [],
  [],
  StockSharedSlice
> = (set, get) => ({
  ...initialState,

  resetSharedStockSlice: () => {
    set(initialState)
  },

  getSuppliers: async (
    forceDatabaseFetch = false,
    includeDeleted = false
  ): Promise<Record<string, ISupplier> | null> => {
    if (Object.keys(get().suppliers).length > 0 && !forceDatabaseFetch)
      return get().suppliers

    try {
      const suppliers = await supplierService.getAllSuppliers(includeDeleted)
      const supplierDict = arrayToDictionary<ISupplier>('id', suppliers)
      set(() => ({
        suppliers: supplierDict,
      }))

      return supplierDict
    } catch (error) {
      set(() => ({
        suppliers: {},
      }))
      exceptionMessageHandler(error, 'Failed to retrieve suppliers')
      return {}
    }
  },

  getItems: async (): Promise<Record<string, IItem> | null> => {
    if (Object.keys(get().items).length > 0) return get().items // Ensure items are loaded before

    try {
      const items = await itemService.getAllItems()
      const itemsDict = arrayToDictionary<IItem>('id', items)
      set(() => ({
        items: itemsDict,
      }))
      return itemsDict
    } catch (error) {
      set(() => ({
        items: {},
      }))
      exceptionMessageHandler(error, 'Failed to retrieve items')
      return {}
    }
  },

  updateItemState: async (updatedItem) => {
    if (Object.keys(get().items).length > 0) return get().items // Ensure items are loaded before
    const updatedItemId = updatedItem.id

    if (!updatedItemId) return null

    set((state) => ({
      items: {
        ...state.items,
        [updatedItemId]: updatedItem,
      },
    }))

    return updatedItem
  },

  getBrands: async (): Promise<Record<string, IBrand> | null> => {
    if (Object.keys(get().brands).length > 0) return get().brands
    try {
      const brands = await brandService.getAllBrands()
      const brandsDict = arrayToDictionary<IBrand>('id', brands)
      set(() => ({
        brands: brandsDict,
      }))
      return brandsDict
    } catch (error) {
      set(() => ({
        brands: {},
      }))
      exceptionMessageHandler(error, 'Failed to retrieve brands')
      return {}
    }
  },

  getStockUnits: async (
    includeDeleted?: boolean
  ): Promise<Record<string, IUnit> | null> => {
    try {
      const units = await unitService.getAllStockUnits(includeDeleted)
      const unitsDict = arrayToDictionary<IUnit>('id', units)
      set(() => ({
        units: unitsDict,
      }))
      return unitsDict
    } catch (error) {
      set(() => ({
        units: {},
      }))
      exceptionMessageHandler(error, 'Failed to retrieve units')
      return {}
    }
  },

  createStocktake: async (
    stocktake: IStocktake
  ): Promise<IStocktake | null> => {
    try {
      const newStocktake = await stocktakeService.createStocktake(stocktake)
      if (!newStocktake) {
        exceptionMessageHandler(
          'No stocktake object was returned.',
          'Failed to create stocktake'
        )
        return null
      }
      return newStocktake
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to create stocktake')
      return null
    }
  },

  createItem: async (item: IItem): Promise<IItem | null> => {
    // Do not want to set isLoading here as it causes the
    // receieve invoice page to have a full page loading screen.
    // Using local states to manage the loading of this.
    try {
      const newItem = await itemService.createItem(item)
      if (!newItem) {
        exceptionMessageHandler(
          'New item was returned with no id',
          'Failed to create item'
        )
        return null
      }

      set((state) => ({
        items: {
          ...state.items,
          [newItem.id!]: new Item(newItem),
        },
      }))
      return newItem
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to create item')
      return null
    }
  },

  createBrand: async (brand: IBrand): Promise<IBrand | null> => {
    try {
      const newBrand = await brandService.createBrand(brand)

      set((state) => ({
        brands: {
          ...state.brands,
          [newBrand.id!]: newBrand,
        },
      }))

      enqueueLoadedSnackbar(
        `The brand ${newBrand.name} has been created.`,
        'success'
      )
      return newBrand
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to create new brand')
      return null
    }
  },

  createSupplier: async (supplier: ISupplier): Promise<ISupplier | null> => {
    try {
      const newSupplier = await supplierService.createSupplier(supplier)

      set((state) => ({
        suppliers: {
          ...state.suppliers,
          [newSupplier.id!]: newSupplier,
        },
      }))

      enqueueLoadedSnackbar(
        `The supplier ${newSupplier.name} has been created.`,
        'success'
      )
      return newSupplier
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to create new supplier')
      return null
    }
  },

  updateSupplier: async (supplier: ISupplier): Promise<ISupplier | null> => {
    try {
      const updatedSupplier = await supplierService.updateSupplier(supplier)

      if (!updatedSupplier) return null

      set((state) => ({
        suppliers: {
          ...state.suppliers,
          [updatedSupplier.id!]: updatedSupplier,
        },
      }))

      enqueueLoadedSnackbar(
        `The supplier ${updatedSupplier.name} has been updated.`,
        'success'
      )
      return updatedSupplier
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update supplier')
      return null
    }
  },

  deleteSupplier: async (supplier: ISupplier): Promise<ISupplier | null> => {
    try {
      const deletedSupplier = await supplierService.deleteSupplier(supplier)

      if (!deletedSupplier) return null

      set((state) => ({
        suppliers: {
          ...state.suppliers,
          [deletedSupplier.id!]: deletedSupplier,
        },
      }))

      enqueueLoadedSnackbar(
        `The supplier ${deletedSupplier.name} has been deleted.`,
        'success'
      )
      return deletedSupplier
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to delete supplier')
      return null
    }
  },

  loadActiveSupplierAliases: async (supplierId: string): Promise<void> => {
    set({ isLoadingActiveSupplierAliases: true, activeSupplierAliases: [] })
    try {
      const aliases = await supplierService.getSupplierAliases(supplierId)
      set({ activeSupplierAliases: aliases })
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to load supplier aliases')
    } finally {
      set({ isLoadingActiveSupplierAliases: false })
    }
  },

  deleteSupplierAlias: async (
    supplierId: string,
    aliasId: string
  ): Promise<boolean> => {
    try {
      await supplierService.deleteSupplierAlias(supplierId, aliasId)
      return true
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to delete supplier alias')
      return false
    }
  },

  restoreSupplier: async (supplier: ISupplier): Promise<ISupplier | null> => {
    try {
      const restoredSupplier = await supplierService.restoreSupplier(supplier)

      if (!restoredSupplier) return null

      set((state) => ({
        suppliers: {
          ...state.suppliers,
          [restoredSupplier.id!]: restoredSupplier,
        },
      }))

      enqueueLoadedSnackbar(
        `The supplier ${restoredSupplier.name} has been restored.`,
        'success'
      )
      return restoredSupplier
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to restore supplier')
      return null
    }
  },

  mergeSuppliers: async (
    sourceId: string,
    destId: string
  ): Promise<{ source: ISupplier; destination: ISupplier } | null> => {
    try {
      const result = await supplierService.mergeSuppliers(sourceId, destId)

      if (!result) return null

      // Update both suppliers in the store
      set((state) => ({
        suppliers: {
          ...state.suppliers,
          [result.source.id!]: result.source,
          [result.destination.id!]: result.destination,
        },
      }))

      enqueueLoadedSnackbar(
        `The supplier ${result.source.name} has been merged into ${result.destination.name}.`,
        'success'
      )
      return result
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to merge suppliers')
      return null
    }
  },

  createStockUnit: async (stockUnit: IUnit): Promise<IUnit | null> => {
    try {
      const newStockUnit = await unitService.createStockUnit(stockUnit)

      set((state) => ({
        units: {
          ...state.units,
          [newStockUnit.id!]: newStockUnit,
        },
      }))

      enqueueLoadedSnackbar(
        'The stock unit ' + newStockUnit.name + ' has been created.',
        'success'
      )

      return newStockUnit
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to create new stock unit')
      return null
    }
  },

  updateItemSupplierVariant: async (
    updatedItemSupplierVariant: IItemSupplierVariant
  ): Promise<IItemSupplierVariant | null> => {
    try {
      const updatedVariant =
        await itemSupplierVariantService.updateItemSupplierVariant(
          updatedItemSupplierVariant
        )

      // Get all items and update local state (Zustand)
      // NOTE if this call becomes expensive feel free to change how we
      // repopulate the state
      const items = await itemService.getAllItems()
      const itemsDict = arrayToDictionary<IItem>('id', items)
      set(() => ({
        items: itemsDict,
      }))

      return updatedVariant
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update item variant')
      return null
    }
  },

  updateItem: async (item: IItem): Promise<IItem | null> => {
    try {
      const updatedItem = await itemService.updateItem(item)

      if (updatedItem) {
        const updatedItemId = updatedItem.id

        if (!updatedItemId)
          throw 'No item id found while updating the item state'

        set((state) => ({
          items: {
            ...state.items,
            [updatedItemId]: updatedItem,
          },
        }))

        return updatedItem
      } else {
        throw 'Failed to update item state'
      }
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update item')
      return null
    }
  },

  patchItemSupplierVariant: async (
    itemSupplierVariantId: string,
    stockItemId?: string,
    brandId?: string,
    unitId?: string
  ): Promise<IItemSupplierVariant | null> => {
    try {
      const updatedItemSupplierVariant =
        await itemSupplierVariantService.patchItemSupplierVariant(
          itemSupplierVariantId,
          stockItemId,
          brandId === '' ? null : brandId,
          unitId
        )

      // Get all items and update local state (Zustand)
      // NOTE if this call becomes expensive feel free to change how we
      // repopulate the state
      const items = await itemService.getAllItems()
      const itemsDict = arrayToDictionary<IItem>('id', items)
      set(() => ({
        items: itemsDict,
      }))

      enqueueLoadedSnackbar('The item variant has been updated.', 'success')

      return updatedItemSupplierVariant
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update item variant')
      return null
    }
  },

  getItemSupplierVariant: async (
    code: string,
    itemId: string,
    supplierId: string
  ): Promise<IItemSupplierVariant | null> => {
    try {
      const variant =
        await itemSupplierVariantService.getItemSupplierVariantViaCodeItemIdAndSupplierId(
          code,
          itemId,
          supplierId
        )

      set((state) => {
        if (!state.items) return { items: state.items }

        const updatedItems = updateOrAddItemSupplierVariantWithinItemStore(
          state.items,
          variant
        )

        return { items: updatedItems }
      })
      return variant
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve variant')
      return null
    }
  },

  removeItemSupplierVariant: async (
    itemSupplierVariantId: string
  ): Promise<boolean> => {
    try {
      await itemSupplierVariantService.removeItemSupplierVariant(
        itemSupplierVariantId
      )

      // Get all items and update local state (Zustand)
      // NOTE if this call becomes expensive feel free to change how we
      // repopulate the state
      // We re-fetch all items rather than manually updating the state
      // beacause we dont know which variant gets updated to the default
      // if we deleted a default variant.
      const items = await itemService.getAllItems()
      const itemsDict = arrayToDictionary<IItem>('id', items)
      set(() => ({
        items: itemsDict,
      }))

      return true
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to remove variant.')
      return false
    }
  },
})

export default createSharedStockSlice
